export type RunStatusCode =
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "PARTIAL"
  | "FAILED"
  | "CANCELLED";

/**
 * Backend may send `error` as a structured object now, not just a string.
 * Always read it through {@link getRunErrorMessage} — rendering the object
 * directly throws "Objects are not valid as a React child".
 */
export interface RunStatusError {
  code: string;
  message: string;
}

export interface RunStatus {
  code: RunStatusCode;
  stage: string | null;
  error: string | RunStatusError | null;
  is_terminal: boolean;
  is_success: boolean;
  is_running: boolean;
}

/** Status-set helpers — keep pollers/UI off hardcoded "active" lists. */
export const TERMINAL_STATUSES: RunStatusCode[] = [
  "SUCCESS",
  "PARTIAL",
  "FAILED",
  "CANCELLED",
];
export const SUCCESS_STATUSES: RunStatusCode[] = ["SUCCESS"];
export const RUNNING_STATUSES: RunStatusCode[] = ["PENDING", "RUNNING"];

/** Safely read a run-status error that may be a string, an object, or null. */
export const getRunErrorMessage = (
  error: string | RunStatusError | null | undefined
): string | null => {
  if (!error) return null;
  if (typeof error === "string") return error;
  return error.message ?? null;
};

export interface RunTiming {
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
}

export interface RunProgress {
  percent: number | null;
  uploaded: number | null;
  total_candidates: number | null;
  stage: string | null;
  stage_title: string | null;
  stage_message: string | null;
  updated_at: string | null;
}

export interface RunDestination {
  code: string;
  label: string;
  project_id: string | null;
}

export interface RunSelectionMachine {
  id: number;
  name: string;
  device_mxid: string;
}

export interface RunSelectionLocation {
  id: number;
  name: string;
}

export interface RunSelectionOrganization {
  id: number;
  name: string;
}

export interface RunSelection {
  machines: RunSelectionMachine[];
  location: RunSelectionLocation | null;
  organization: RunSelectionOrganization | null;
}

export interface RunSummary {
  uploaded: number | null;
  failed: number | null;
  selection: RunSelection;
}

/**
 * What a `dataset` run produced: a manifest in the mlops bucket rather than
 * images copied into a provider project. Metadata-only, so these runs finish
 * in seconds and the thing worth surfacing is the URI to hand to a trainer.
 *
 * Only present on dataset-destination runs. Requires the BE half (MIN-259).
 */
export interface RunArtifact {
  /** Fully-qualified gs:// URI of the manifest. */
  uri: string;
  /** Rows written to the manifest. */
  record_count: number | null;
  /** Content hash, for pinning a training run to an exact manifest. */
  manifest_hash: string | null;
}

/** Destination code for metadata-only manifest exports. */
export const DATASET_DESTINATION = "dataset";

// --- Plan / Metrics types ---

export interface PlanOverview {
  total_candidates: number;
  total_buckets: number;
  total_machines: number;
  dominant_label: string | null;
  dominant_label_share: number | null;
}

export interface PlanBucketMachine {
  id: number;
  name: string;
  device_mxid: string;
}

export interface PlanBucketCandidates {
  count: number;
  share_of_index: number;
}

export interface PlanBucketSpan {
  start: string;
  end: string;
}

export interface PlanBucketQuality {
  mistakes_count: number;
  mistakes_share_of_bucket: number;
}

export interface PlanBucket {
  machine: PlanBucketMachine;
  candidates: PlanBucketCandidates;
  labels: Record<string, number>;
  splits: Record<string, number>;
  span: PlanBucketSpan;
  quality: PlanBucketQuality;
}

export interface PlanCost {
  total_candidates: number;
  mode: "approx" | "actual";
  estimated_bytes: number | null;
  gcs_egress_gb: number | null;
  gcs_egress_cost_usd: number | null;
  sampled_count: number;
  missing_count: number;
  actual_bytes: number | null;
  actual_gcs_egress_gb: number | null;
  actual_gcs_egress_cost_usd: number | null;
  max_bytes_per_run: number;
  bytes_over_limit: boolean;
}

export interface PlanLimits {
  images_used: number;
  images_max?: number;
  images_over_limit?: boolean;
  /** Images the plan request asked for — the full selection, not one run. */
  images_requested?: number;
  /** Hard per-create-run cap the backend enforces (200 today). */
  images_max_per_run?: number;
  /** Chunk size the backend wants the selection split into. */
  recommended_chunk_size?: number;
  /** How many chunks that split produces. */
  recommended_chunk_count?: number;
  bytes_used: number;
  bytes_max: number;
  bytes_over_limit: boolean;
  over_any_limit: boolean;
}

/**
 * A non-fatal note on the plan. `image_limit_exceeded` is the one that drives
 * the chunked export: the plan still returns 200, and `detail` is the sentence
 * to show the user (prefer it over anything the frontend composes).
 */
export interface PlanWarning {
  code: string;
  detail: string;
  recommended_chunk_size?: number;
  recommended_chunk_count?: number;
}

/** Warning code that means "split this into several runs". */
export const IMAGE_LIMIT_WARNING_CODE = "image_limit_exceeded";

/**
 * Diagnostic info from the selector — explains WHY total_candidates
 * may be 0. Optional (null for older selectors that don't emit it).
 *
 * `rejection_reason` is a stable machine code; drive UI off that, not
 * off the raw counts. Counts are provided so we can show a detailed
 * funnel when helpful.
 */
export type PlanSelectorRejectionReason =
  | "INPUT_NO_IMAGES"
  | "INPUT_IMAGES_NOT_FOUND"
  | "SCOPE_OWNERSHIP_DENIED"
  | "DATA_NO_CLASSIFICATIONS"
  | "DATA_LEGACY_LABELS_ONLY"
  | "DATA_BINARY_PAIR_CONFLICT"
  | "DATA_METADATA_INCOMPLETE"
  | "FILTER_CONFIDENCE_EXCLUDED_ALL"
  | "FILTER_ONLY_MISTAKES_EMPTY"
  | "UNKNOWN";

export interface PlanSelectorDiagnostics {
  input: {
    requested: number;
    found_in_db: number;
  };
  scope: {
    ownership_denied: number;
  };
  data: {
    no_classifications: number;
    legacy_labels_only: number;
    binary_pair_conflict: number;
    metadata_incomplete: number;
  };
  filters: {
    confidence_excluded: number;
    only_mistakes_excluded: number;
  };
  yielded: number;
  rejection_reason: PlanSelectorRejectionReason | null;
  rejection_message: string | null;
}

export interface RunMetrics {
  overview: PlanOverview;
  labels_overall: Record<string, number>;
  splits_overall: Record<string, number>;
  buckets: PlanBucket[];
  cost: PlanCost;
  limits: PlanLimits;
  selector?: PlanSelectorDiagnostics | null;
}

/** Response from POST /runs/plan/ — data field */
export interface MLExportRunPlan {
  overview: PlanOverview;
  labels_overall: Record<string, number>;
  splits_overall: Record<string, number>;
  buckets: PlanBucket[];
  cost: PlanCost;
  limits: PlanLimits;
  selector?: PlanSelectorDiagnostics | null;
  warnings?: PlanWarning[];
}

/** Response from POST /runs/, GET /runs/{id}/, GET /runs/history/ — data field */
export interface MLExportRun {
  run_id: string;
  organization_id: number;
  task_kind: string;
  trigger: string;
  input_mode: string;
  destination: RunDestination;
  status: RunStatus;
  timing: RunTiming;
  progress: RunProgress;
  metrics: RunMetrics | null;
  summary: RunSummary;
  /** Manifest produced by a `dataset` run. See {@link RunArtifact}. */
  artifact?: RunArtifact | null;
}

/**
 * Input for POST /runs/plan/ and POST /runs/ — both take the same body.
 *
 * `task_kind` + `destination` are derived from the selected project; the
 * frontend sends `project_key` (never the legacy `project_id` alias). At least
 * one scope selector (machine_ids preferred, else device_mxids or location_id)
 * must be present.
 */
export interface RunCreateInput {
  task_kind: string;
  image_ids: number[];
  destination?: string;
  project_key?: string;
  location_id?: number;
  machine_ids?: number[];
  device_mxids?: string[];
  /**
   * Omitted / false → backend excludes only frames explicitly marked
   * dump_image_valid=false. true → only frames explicitly marked valid.
   */
  require_valid_frames?: boolean;
  /** Create-only; backend default is "adhoc_batch" when omitted. */
  trigger?: string;
}

/**
 * Hard per-run image caps the backend enforces, keyed by destination. A run
 * over the cap is rejected at plan/create time with `image_limit_exceeded`
 * (the error's `context.max_images` is the source of truth). Roboflow caps at
 * 200; destinations not listed here have no FE-side cap. Verified live on dev.
 */
export const DESTINATION_MAX_IMAGES: Record<string, number> = {
  roboflow: 200,
};

/** Per-run image cap for a destination, or Infinity when uncapped. */
export const maxImagesForDestination = (
  destination?: string | null
): number =>
  (destination ? DESTINATION_MAX_IMAGES[destination] : undefined) ??
  Number.POSITIVE_INFINITY;

/**
 * Absolute ceiling on images in one POST /runs/. The plan is the source of
 * truth for how to split a selection, but no chunk may ever exceed this — the
 * backend rejects an over-cap create with `image_limit_exceeded`.
 */
export const HARD_MAX_IMAGES_PER_RUN = 200;

/**
 * How large one export run may be. The plan decides
 * (`recommended_chunk_size`, else `images_max_per_run`); {@link
 * DESTINATION_MAX_IMAGES} is only the fallback before a plan arrives. Always
 * clamped to {@link HARD_MAX_IMAGES_PER_RUN}.
 */
export const resolveRunChunkSize = (
  limits?: PlanLimits | null,
  destination?: string | null
): number => {
  const fromPlan = limits?.recommended_chunk_size ?? limits?.images_max_per_run;
  const size =
    fromPlan != null && Number.isFinite(fromPlan) && fromPlan > 0
      ? fromPlan
      : maxImagesForDestination(destination);
  return Math.min(size, HARD_MAX_IMAGES_PER_RUN);
};

/**
 * Split image ids into runs no larger than `size`. A non-finite / non-positive
 * size, or a selection that already fits, yields a single run.
 */
export const chunkImageIds = (
  imageIds: number[],
  size: number
): number[][] => {
  if (!Number.isFinite(size) || size <= 0 || imageIds.length <= size) {
    return [imageIds];
  }
  const chunks: number[][] = [];
  for (let i = 0; i < imageIds.length; i += size) {
    chunks.push(imageIds.slice(i, i + size));
  }
  return chunks;
};
